import React, { useEffect, useRef, useState } from "react";
import usePageState from "utils/usePageState";
import { OnlineMerchantManagement } from "components/OnlineMerchant/OnlineMerchantManagement";
import Divider from "components/Divider/Divider";
import { useForm, useWatch } from "react-hook-form";
import { FormBuilder } from "components/FormBuilder/FormBuilder";
import { IconButton, Stack, Tab, Typography } from "@mui/material";
import { LoadingButton, TabContext, TabList, TabPanel } from "@mui/lab";
import useFormUndo from "utils/useFormUndo/useFormUndo";
import useSnackbarMAS from "utils/useSnackbarMAS/useSnackbarMAS";
import { deepEqual, formPreventEnterSubmit } from "utils/utils";
import { Edit, Refresh } from "@mui/icons-material";
import useColors from "utils/useColors/useColors";
import StatusWrapper from "components/StatusWrapper/StatusWrapper";
import dayjs from "dayjs";
import { spinning } from "../offline-merchant-detail/utils";
import useDialogManager from "utils/useDialogManager/useDialogManager";
import { useTranslation } from "react-i18next";
import usePageWrapper from "utils/usePageWrapper/usePageWrapper";

const parseStatusReasonOptions = (obj) => {
  const options = Object.entries(obj).map(([key, value]) => ({
    key,
    title: value,
    value: key
  }))
  options.push({
    key: "OTHERS",
    title: "Others",
    value: "OTHERS"
  })
  return options
}

const TAB_LIST_STYLE = {
  "& .MuiTabs-indicator": {
    backgroundColor: "#309ea7",
  },
  "& .MuiTab-root": {
    whiteSpace: "nowrap",
  },
};
const TAB_STYLE = {
  "&.Mui-selected": {
    color: "#309ea7",
  },
  textTransform: "none",
};
const TAB_PANEL_STYLE = { padding: 0 }

const OnlineMerchantDetail = ({ path, redirectFn }) => {
  const { t: tLocal } = useTranslation("translation", { keyPrefix: "page.merchantManagement.onlineMerchantDetail" });
  const { t: tCommon } = useTranslation();
  const c = useColors();
  const pageState = usePageState({ pagePath: path });
  const { snackbar, snackbarError, snackbarSuccess } = useSnackbarMAS();
  const formProps = useForm({
    mode: "onChange",
  });
  const { handleSubmit } = formProps;
  const formUndoProps = useFormUndo({ formProps });
  const { memorizedFormValue, rememberForm, undoForm } = formUndoProps;
  const { handleApi, PageWrapper, PageOuterContainer, PageStickyHeader, PageStickyFooter } = usePageWrapper();

  const midRef = useRef(null)
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [isReloading, setIsReloading] = useState(true);
  const [currentTab, setCurrentTab] = useState("info");
  const [isEditing, setIsEditing] = useState(false);

  const [merchantName, setMerchantName] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [suspensionRemarks, setSuspensionRemarks] = useState({});

  const merchantId = pageState.merchantId;
  const allowUsdTransacton = !!(useWatch({ control: formProps.control, name: "currencyList" })?.includes("USD"));
  const merchantStatus = useWatch({ control: formProps.control, name: "status" });
  const isLoading = isFirstLoading || isReloading;

  useEffect(() => {
    if (!merchantId) {
      redirectFn("close", { tabId: path });
    } else {
      if (midRef.current !== merchantId) {
        // special handling to prevent multiple initialization (might occur when page is refreshed)
        midRef.current = merchantId
        fetchData({ isInit: true });
      }
    }
  }, [pageState]);

  /**
   * @param {Object} props
   * @param {boolean} props.isInit - pass true if initialization
   * @param {boolean} props.showError - pass true if show error
   * @returns {Promise<boolean>} - true if success
   */
  const fetchData = async ({ isInit, showError } = {}) => {
    isInit && setIsFirstLoading(true);
    setIsReloading(true);

    if (isInit) {
      const [dropdownOptionResult, merchantSuspensionRemarks] = await Promise.all([
        OnlineMerchantManagement.Api.getDropdownOptions(),
        OnlineMerchantManagement.Api.getAccountSuspensionRemarks()
      ]);

      // const [apiA, apiB] = await handleApiCall([
      //   { api: OnlineMerchantManagement.Api.getDropdownOptions(), handler: (resp) => resp.fail && tLocal("error.options.failed") },
      //   OnlineMerchantManagement.Api.getAccountSuspensionRemarks()
      // ])

      // const result = handleApi(OnlineMerchantManagement.Api.getDropdownOptions())
      // const result = handleApi(OnlineMerchantManagement.Api.getDropdownOptions())
      // result = {
      //   respCode: "",
      //   isUnauth,
      //   isExpire,
      // }

      if (!handleApi(dropdownOptionResult) || !handleApi(merchantSuspensionRemarks)) {
        snackbarError({ message: tLocal("toast.options.failed") });
        return false;
      }

      const dropdown = dropdownOptionResult.data.data;
      setDropdownOptions({
        serviceIntegratorList: dropdown?.serviceIntegratorList,
        referrerList: dropdown?.referrerList,
        salesList: dropdown?.salesList,
        mccList: dropdown?.mccList,
      });

      const suspensionRemarks = merchantSuspensionRemarks.data.data;
      setSuspensionRemarks({
        terminated: suspensionRemarks["TERMINATED"],
        suspended: suspensionRemarks["SUSPENDED"],
      })
    }

    const [merchantEntity, merchantMdr, merchantProcessorConfig] = await Promise.all([
      OnlineMerchantManagement.Api.getMerchantEntity({ merchantId }),
      OnlineMerchantManagement.Api.getMerchantMdr({ merchantId }),
      OnlineMerchantManagement.Api.getMerchantProcessorConfig({ merchantId }),
    ])

    if (
      !handleApi(merchantEntity) ||
      !handleApi(merchantMdr) ||
      !handleApi(merchantProcessorConfig, { allowedResCodes: ["0000", "2408"] })
    ) {
      (isInit || showError) && snackbarError({ message: tLocal("toast.getDetail.failed") });
      return false;
    }

    const mdrFormData = OnlineMerchantManagement.PayloadParser.merchantMdr(merchantMdr.data.data);
    const formData = {
      ...OnlineMerchantManagement.PayloadParser.merchantEntity(merchantEntity.data.data),
      ...mdrFormData,
      ...OnlineMerchantManagement.PayloadParser.merchantProcessorConfig(merchantProcessorConfig.data.data),
    };

    formProps.reset(formData);
    rememberForm(formData);
    setMerchantName(formData.name);
    // updateTime is merchant entity, updated is merchant mdr
    // get the latest one as last updated
    const laterUpdated = dayjs(formData.updateTime).isAfter(dayjs(formData.updated)) ? formData.updateTime : formData.updated;
    setLastUpdated(dayjs(laterUpdated).format("YYYY-MM-DD HH:mm:ss"));

    isInit && setIsFirstLoading(false);
    setIsReloading(false);

    return true;
  }

  const onSubmit = async (formValues) => {
    if (deepEqual(memorizedFormValue, formValues)) {
      snackbar({ message: tCommon("component.form.undo.identical"), variant: "info" });
      return;
    };

    setIsReloading(true);

    const allowUsdTransactions = formValues.currencyList?.includes("USD");
    const settleByUsd = formValues.usdSettleCurrency === "USD";
    const hasNuveiSiteId = !!formValues.siteId;

    // const test = false;
    // console.log("formValues", formValues);
    // if (test) {
    //   console.log("update merchant payload", OnlineMerchantManagement.PayloadBuilder.updateMerchantEntity(formValues));
    //   hasNuveiSiteId && console.log("update processor config", OnlineMerchantManagement.PayloadBuilder.updateNuveiConfig(formValues));
    //   allowUsdTransactions && console.log("update merchant settle currency", OnlineMerchantManagement.PayloadBuilder.updateMerchantSettleCurrency(formValues));
    //   allowUsdTransactions && settleByUsd && console.log("update merchant settle currency info", OnlineMerchantManagement.PayloadBuilder.updateMerchantSettleCurrencyInfo(formValues));
    //   console.log("update merchant mdr", OnlineMerchantManagement.PayloadBuilder.updateMerchantMdr(formValues));
    //   return;
    // }

    const updateEntityResult = await OnlineMerchantManagement.Api.updateMerchantEntity(OnlineMerchantManagement.PayloadBuilder.updateMerchantEntity(formValues));
    if (!handleApi(updateEntityResult)) {
      snackbarError({ message: tLocal("toast.updateDetail.failed") });
      setIsReloading(false);
      return;
    }

    const [updateProcessorConfigResult, updateSettleCurrencyResult, updateSettleCurrencyInfoResult, updateMdrResult] = await Promise.all([
      hasNuveiSiteId && OnlineMerchantManagement.Api.updateMerchantProcessorConfig(OnlineMerchantManagement.PayloadBuilder.updateNuveiConfig(formValues)),
      allowUsdTransactions && OnlineMerchantManagement.Api.updateMerchantSettleCurrency(OnlineMerchantManagement.PayloadBuilder.updateMerchantSettleCurrency(formValues)),
      allowUsdTransactions && settleByUsd && OnlineMerchantManagement.Api.updateMerchantSettleCurrencyInfo(OnlineMerchantManagement.PayloadBuilder.updateMerchantSettleCurrencyInfo(formValues)),
      OnlineMerchantManagement.Api.updateMerchantMdr(OnlineMerchantManagement.PayloadBuilder.updateMerchantMdr(formValues, "ALL")),
    ])

    let isAllApiCallSuccess = true;

    if (updateProcessorConfigResult && !handleApi(updateProcessorConfigResult)) {
      isAllApiCallSuccess = false;
      snackbarError({ message: tLocal("toast.updateProcessorConfig.failed") });
    }

    if (updateSettleCurrencyResult && !handleApi(updateSettleCurrencyResult)) {
      isAllApiCallSuccess = false;
      snackbarError({ message: tLocal("toast.updateSettleCurrency.failed") });
    }

    if (updateSettleCurrencyInfoResult && !handleApi(updateSettleCurrencyInfoResult)) {
      isAllApiCallSuccess = false;
      snackbarError({ message: tLocal("toast.updateSettleCurrencyInfo.failed") });
    }

    if (!handleApi(updateMdrResult)) {
      isAllApiCallSuccess = false;
      snackbarError({ message: tLocal("toast.updateSettleInfo.failed") });
    }

    if (isAllApiCallSuccess) {
      snackbarSuccess({ message: tLocal("toast.updateDetail.success") });
      await fetchData({ showError: true });
      setIsEditing(false);
    }
    setIsReloading(false);
  }

  const statusDialogFormProps = useForm({
    mode: "onChange",
    defaultValues: {
      status: merchantStatus,
      lockAccountType: formProps.getValues("lockAccountType"),
      remark: formProps.getValues("remark")
    }
  });
  const { dialog, openDialog } = useDialogManager({
    title: tCommon("form.onlineMerchantManagement.components.accountStatusDialog.title"),
    getContent: ({ disabled, loading }) => <OnlineMerchantManagement.Components.StatusDialog
      currentStatus={{
        status: merchantStatus,
        lockAccountType: formProps.getValues("lockAccountType"),
        remark: formProps.getValues("remark")
      }}
      formProps={statusDialogFormProps}
      suspendedOptions={parseStatusReasonOptions(suspensionRemarks.suspended ?? {})}
      terminatedOptions={parseStatusReasonOptions(suspensionRemarks.terminated ?? {})}
      loading={loading}
      disabled={disabled}
    />,
    onPositiveAction: async () => {
      if (!(await statusDialogFormProps.trigger())) {
        return false;
      }
      const data = statusDialogFormProps.getValues();
      const result = await OnlineMerchantManagement.Api.updateMerchantEntity(OnlineMerchantManagement.PayloadBuilder.updateMerchantStatus({ merchantId, ...data }, suspensionRemarks));
      if (!handleApi(result)) {
        snackbarError({ message: tLocal("toast.updateStatus.failed") });
      } else {
        snackbarSuccess({ message: tLocal("toast.updateStatus.success") });
      }
      fetchData({ isInit: true });
      return true;
    },
    onOpen: () => {
      statusDialogFormProps.reset();
      return true;
    },
    config: {
      positiveActionText: tCommon("component.simple.dialog.action.3"),
      negativeActionText: tCommon("component.simple.dialog.action.4"),
    }
  })

  const info = () => {
    if (isFirstLoading) {
      return spinning();
    }
    return <form noValidate onSubmit={handleSubmit(onSubmit)} onKeyDown={formPreventEnterSubmit}>
      <FormBuilder.Section>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
          {merchantStatus === "ACTIVE" ? <div /> : <OnlineMerchantManagement.Components.AccountStatusAlert
            status={merchantStatus}
            remark={formProps.getValues("remark")}
            date={formProps.getValues("terminateTime")}
          />}
          <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={2}>
            <Typography variant="content-s" color={c["blackDark999"]}>{tLocal("label.lastUpdated", { time: lastUpdated })}</Typography>
            <LoadingButton
              key="reload"
              variant="outline-normal-button"
              type="button"
              onClick={() => {
                undoForm();
                fetchData()
              }}
              startIcon={<Refresh />}
              loading={isLoading}
              disabled={isEditing}
            >
              {tCommon("common.refresh")}
            </LoadingButton>
          </Stack>
        </Stack>

        <OnlineMerchantManagement.MerchantForm.BasicInfoSection
          formProps={formProps}
          isEditing={isEditing}
          isLoading={isLoading}
          mccDropdownOptions={(dropdownOptions?.mccList ?? []).map((mccObj) => ({
            key: mccObj.key,
            title: mccObj.title,
            value: mccObj.key,
          }))} />
        <Divider />
        <OnlineMerchantManagement.MerchantForm.CheckoutConfigSection
          formProps={formProps}
          isEditing={isEditing}
          isLoading={isLoading}
        />
        <Divider />
        <OnlineMerchantManagement.MerchantForm.OtherSection
          formProps={formProps}
          isEditing={isEditing}
          isLoading={isLoading}
          salesOptions={(dropdownOptions?.salesList ?? []).map((salesObj) => ({
            key: salesObj.id,
            title: salesObj.firstName,
            value: salesObj.id,
          }))}
          referrerOptions={(dropdownOptions?.referrerList ?? []).map((referrerObj) => ({
            key: referrerObj.id,
            title: referrerObj.company,
            value: referrerObj.id,
          }))}
        />
        <Divider />
        <OnlineMerchantManagement.MerchantForm.BankSection
          formProps={formProps}
          isEditing={isEditing}
          isLoading={isLoading}
        />
        <Divider />
        <OnlineMerchantManagement.MerchantForm.FinanceSection
          formProps={formProps}
          isEditing={isEditing}
          isLoading={isLoading}
        />
        <Divider />
        <OnlineMerchantManagement.MerchantForm.SettlementSection
          formProps={formProps}
          isEditing={isEditing}
          isLoading={isLoading}
        />
        {
          allowUsdTransacton &&
          <>
            <Divider />
            <OnlineMerchantManagement.MerchantForm.UsdSettlementSection
              formProps={formProps}
              isEditing={isEditing}
              isLoading={isLoading}
            />
          </>
        }
        <Divider />
        <OnlineMerchantManagement.MerchantForm.ProcessorEnableSection
          formProps={formProps}
          isEditing={isEditing}
          isLoading={isLoading}
        />
        <Divider />
        <OnlineMerchantManagement.MerchantForm.PaymentConfigSection
          formProps={formProps}
          isEditing={isEditing}
          isLoading={isLoading}
        />
        <Divider />
        <OnlineMerchantManagement.MerchantForm.MdrSection
          formProps={formProps}
          isEditing={isEditing}
          isLoading={isLoading}
        />
      </FormBuilder.Section>
      <PageStickyFooter>
        <Stack
          direction="row"
          justifyContent="flex-end"
          gap={2}
        >
          {!isEditing ? (
            <LoadingButton
              key="edit"
              variant="normal-button"
              type="button"
              onClick={() => {
                setIsEditing(true)
                rememberForm();
              }}
              disabled={isLoading}
            >
              {tCommon("common.edit")}
            </LoadingButton>
          ) : (
            <>
              <LoadingButton
                key="cancel"
                variant="outline-normal-button"
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  undoForm();
                }}
                disabled={isLoading}
              >
                {tCommon("common.cancel")}
              </LoadingButton>
              <LoadingButton
                key="submit"
                variant="normal-button"
                type="submit"
                loading={isLoading}
              >
                {tCommon("common.submit")}
              </LoadingButton>
            </>)}
        </Stack>
      </PageStickyFooter>
    </form>
  }

  const employeeFormProps = useForm({ mode: "onChange", defaultValues: { merchant_id: merchantId } });
  const employee = () => {
    return <OnlineMerchantManagement.MerchantForm.EmployeeSection
      redirectFn={redirectFn}
      formProps={employeeFormProps}
    />
  }

  return (
    <PageOuterContainer>
      {dialog}
      <TabContext value={currentTab}>
        <PageStickyHeader noPadding>
          <Typography variant="h2">{tLocal("label.merchantName", { name: merchantName || "-" })}</Typography>
          <Stack direction="row" justifyContent="space-between">
            <Typography>{tLocal("label.id", { id: merchantId })}</Typography>
            <Stack direction="row" gap={1} alignItems="center">
              <Typography>{tLocal("label.status")}</Typography>
              <StatusWrapper
                size="small"
                status={
                  ["ACTIVE", "SUSPENDED", "TERMINATED"].includes(merchantStatus)
                    ? merchantStatus
                    : "———"
                }
              />
              {merchantStatus !== "TERMINATED" && <IconButton
                size="small"
                onClick={() => {
                  openDialog();
                }}
                disabled={isLoading || isEditing}
              >
                <Edit fontSize="small" />
              </IconButton>}
            </Stack>
          </Stack>
          <TabList
            onChange={(event, newValue) => setCurrentTab(newValue)}
            sx={TAB_LIST_STYLE}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Info" value="info" sx={TAB_STYLE} />
            <Tab label="Employees" value="employee" sx={TAB_STYLE} />
          </TabList>
        </ PageStickyHeader>
        <PageWrapper noPadding isLoading={isFirstLoading && currentTab === "info"}>
          <TabPanel value="info" style={TAB_PANEL_STYLE}>
            {info()}
          </TabPanel>
          <TabPanel value="employee" style={TAB_PANEL_STYLE}>
            {employee()}
          </TabPanel>
        </PageWrapper>
      </TabContext>
    </ PageOuterContainer>
  )
};

export default React.memo(OnlineMerchantDetail);
